import type { ReactNode } from 'react';
import { useIntl, type MessageDescriptor } from '@atlassian/jira-intl';
import { getDateFromTimestamp } from '@atlassian/jira-software-roadmap-utils/src/utils/dates.tsx';
import { INCOMING, OUTGOING } from '../../../common/constants/dependency.tsx';
import type {
	Appearance,
	ImplicitDependencyItem,
	OverlayItem,
} from '../../../common/types/dependency.tsx';
import { isDependencyDatesOverlapping } from '../../../common/utils/dependency/overlap.tsx';
import implicitDependencyMessages from './implicit-dependencies/messages.tsx';
import messages from './messages.tsx';

export const useDependencyFlyoutLabel = (
	item: OverlayItem,
	deps: OverlayItem[],
	issueAppearance: Appearance,
	dependencyType: string,
) => {
	const { formatMessage } = useIntl();

	if (!deps.length) {
		return '';
	}

	const createIssueLabelDesc = (
		typeName: string | undefined,
		title: string,
		startDate: number | undefined,
		dueDate: number | undefined,
		appearance: Appearance,
		isOverlapping?: boolean,
		warningMessages?: MessageDescriptor[],
	) => {
		const startDateIssueLabel = formatMessage(messages.issueWithStartDateLabel, {
			issueType: typeName,
			issueTitle: title,
			issueDate: startDate !== undefined ? getDateFromTimestamp(startDate) : undefined,
		});

		const dudeDateIssueLabel = formatMessage(messages.issueWithDueDateLabel, {
			issueType: typeName,
			issueTitle: title,
			issueDate: dueDate !== undefined ? getDateFromTimestamp(dueDate) : undefined,
		});
		const issueLabel = appearance === INCOMING ? startDateIssueLabel : dudeDateIssueLabel;

		const warnings = warningMessages?.length
			? warningMessages.map((message) => formatMessage(message)).join(', ')
			: '';

		const overlapWarning = isOverlapping ? formatMessage(messages.datesOverlapWarning) : '';

		const result =
			isOverlapping || warnings
				? `${issueLabel} ${formatMessage(messages.warningsText)}: ${overlapWarning} ${warnings}`
				: issueLabel;

		return result;
	};

	const itemLabel = createIssueLabelDesc(
		item.typeName,
		item.title,
		item.startDate,
		item.dueDate,
		issueAppearance,
	);

	const depsLabels = deps
		.map((dep: OverlayItem) => {
			const isOverlapping =
				issueAppearance === INCOMING
					? isDependencyDatesOverlapping(dep.startDate, dep.dueDate, item.startDate, item.dueDate)
					: isDependencyDatesOverlapping(item.startDate, item.dueDate, dep.startDate, dep.dueDate);

			return createIssueLabelDesc(
				dep.typeName,
				dep.title,
				dep.startDate,
				dep.dueDate,
				issueAppearance === INCOMING ? OUTGOING : INCOMING,
				isOverlapping,
				dep.warningMessages,
			);
		})
		.join(' ');

	const labelDesc = `${itemLabel} ${dependencyType}: ${depsLabels}`;

	return labelDesc;
};

export const useImplicitDependencyLabel = (
	itemKey: string | undefined,
	title: string,
	implicitDependencyItems: ImplicitDependencyItem[],
) => {
	const { formatMessage } = useIntl();

	const { incoming, outgoing } = implicitDependencyItems.reduce<{
		incoming: ImplicitDependencyItem[];
		outgoing: ImplicitDependencyItem[];
	}>(
		(acc, item: ImplicitDependencyItem) => {
			if (item.dependencies.length > 0) {
				acc.incoming.push(item);
			}
			if (item.dependees.length > 0) {
				acc.outgoing.push(item);
			}
			return acc;
		},
		{ incoming: [], outgoing: [] },
	);

	if (incoming.length === 0 && outgoing.length === 0) {
		return '';
	}

	const dependencyItemsLabel = (
		items: ImplicitDependencyItem[],
		messageDescriptorSingular: MessageDescriptor,
		messageDescriptorPlural: MessageDescriptor,
	) => {
		if (!items.length) {
			return '';
		}

		const messageDescriptor =
			items.length === 1 ? messageDescriptorSingular : messageDescriptorPlural;

		return formatMessage(
			{ ...messageDescriptor },
			{
				count: items.length,
				itemKey,
				title,
				b: (chunks: string | number | ReactNode) => chunks,
			},
		);
	};

	const incomingLabel = dependencyItemsLabel(
		incoming,
		implicitDependencyMessages.singleIncomingDependency,
		implicitDependencyMessages.incomingDependencies,
	);
	const outgoingLabel = dependencyItemsLabel(
		outgoing,
		implicitDependencyMessages.singleOutgoingDependency,
		implicitDependencyMessages.outgoingDependencies,
	);

	return `${incomingLabel}\n${outgoingLabel}`;
};
